import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CreditCard, Link2, Clock, ChevronRight, Check, Truck, Store, User, LogIn, CalendarDays, ChevronLeft } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { ApplePayLogo, GooglePayLogo, InteracLogo, SezzleLogo, VisaLogo, MastercardLogo } from '@/components/PaymentLogos';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const DAYS_FR = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
const MONTHS_FR = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

function MiniCalendar({ selected, onSelect }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [viewYear, setViewYear] = useState(today.getFullYear());

  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const isDisabled = (d) => {
    if (!d) return true;
    const date = new Date(viewYear, viewMonth, d);
    date.setHours(0, 0, 0, 0);
    return date < today;
  };

  const isSelected = (d) => {
    if (!d || !selected) return false;
    return selected === `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  };

  const isTuesday = (d) => {
    if (!d) return false;
    return new Date(viewYear, viewMonth, d).getDay() === 2;
  };

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  return (
    <div className="rounded-2xl p-4" style={{ backgroundColor: '#121611', border: '1px solid #2C3529' }}>
      <div className="flex items-center justify-between mb-3">
        <button onClick={prevMonth} className="p-1.5 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors">
          <ChevronLeft size={16} />
        </button>
        <span className="text-sm font-semibold text-white">{MONTHS_FR[viewMonth]} {viewYear}</span>
        <button onClick={nextMonth} className="p-1.5 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors">
          <ChevronRight size={16} />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {DAYS_FR.map(d => <div key={d} className="text-center text-[10px] font-medium text-white/30 py-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => (
          <button key={i} disabled={isDisabled(d)}
            onClick={() => d && !isDisabled(d) && onSelect(`${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`)}
            className={`h-9 rounded-lg text-xs font-medium transition-all ${
              !d ? '' :
              isSelected(d) ? 'bg-[#F78A7D] text-white scale-110 shadow-lg' :
              isDisabled(d) ? 'text-white/10 cursor-not-allowed' :
              isTuesday(d) ? 'bg-[#6DAE65]/20 text-[#6DAE65] hover:bg-[#6DAE65]/30 font-bold' :
              'text-white/60 hover:bg-white/10 hover:text-white'
            }`}>
            {d || ''}
          </button>
        ))}
      </div>
      <p className="text-[10px] text-center mt-2 flex items-center justify-center gap-1.5">
        <span className="w-2 h-2 rounded-full bg-[#6DAE65]/40 inline-block"></span>
        <span className="text-white/30">Mardis = jours de livraison</span>
      </p>
    </div>
  );
}

const PAYMENT_METHODS = [
  { id: 'card', logo: () => <div className="flex gap-1"><VisaLogo size={18} /><MastercardLogo size={18} /></div>, label: 'Carte de crédit / débit', desc: 'Visa, Mastercard, Amex' },
  { id: 'apple', logo: () => <ApplePayLogo size={20} />, label: 'Apple Pay', desc: 'Touch ID / Face ID' },
  { id: 'google', logo: () => <GooglePayLogo size={20} />, label: 'Google Pay', desc: 'Paiement sécurisé Google' },
  { id: 'interac', logo: () => <InteracLogo size={20} />, label: 'Virement Interac direct', desc: 'Depuis votre compte bancaire' },
  { id: 'link', icon: Link2, label: 'Envoyer un lien de paiement', desc: 'Par courriel ou SMS' },
  { id: 'sezzle', logo: () => <SezzleLogo size={20} />, label: 'Sezzle', desc: 'Acheter maintenant, payer en 4 versements' },
];

export default function CheckoutModal({ open, onClose }) {
  const { items, total, clearCart } = useCart();

  useEffect(() => {
    if (open) {
      const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
      window.addEventListener('keydown', handleEsc);
      return () => window.removeEventListener('keydown', handleEsc);
    }
  }, [open, onClose]);
  const [step, setStep] = useState(0); // 0=login, 1=payment, 2=details, 3=confirm
  const [method, setMethod] = useState(null);
  const [delivery, setDelivery] = useState('pickup');
  const [form, setForm] = useState({ name: '', email: '', phone: '', address: '', date: '', notes: '' });
  const [confirmed, setConfirmed] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // login or register
  const [authForm, setAuthForm] = useState({ email: '', password: '', name: '', phone: '', type: 'individual', company: '' });
  const [authError, setAuthError] = useState('');
  const [user, setUser] = useState(null);
  const [zones, setZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [useWallet, setUseWallet] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);

  const formatPhone = (val) => {
    const digits = val.replace(/\D/g, '').slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  useEffect(() => {
    if (open) axios.get(`${API}/api/delivery-zones`).then(r => setZones(r.data)).catch(() => {});
  }, [open]);

  useEffect(() => {
    const saved = localStorage.getItem('cg-customer');
    if (saved) {
      try {
        const u = JSON.parse(saved);
        setUser(u);
        setForm(f => ({ ...f, name: u.name, email: u.email, phone: u.phone || '' }));
        setStep(1);
        if (u.id) axios.get(`${API}/api/customers/${u.id}/wallet`).then(r => setWalletBalance(r.data.balance || 0)).catch(() => {});
      } catch {}
    }
  }, [open]);

  const handleAuth = async () => {
    setAuthError('');
    try {
      if (authMode === 'register') {
        const { data } = await axios.post(`${API}/api/customers/register`, authForm);
        const u = { id: data.id, name: authForm.name, email: authForm.email, phone: authForm.phone, type: authForm.type, company: authForm.company, token: data.token };
        localStorage.setItem('cg-customer', JSON.stringify(u));
        setUser(u);
        setForm(f => ({ ...f, name: u.name, email: u.email, phone: u.phone }));
        setStep(1);
      } else {
        const { data } = await axios.post(`${API}/api/customers/login`, { email: authForm.email, password: authForm.password });
        const u = { id: data.id, name: data.name, email: data.email, phone: data.phone || '', type: data.type, company: data.company || '', token: data.token };
        localStorage.setItem('cg-customer', JSON.stringify(u));
        setUser(u);
        setForm(f => ({ ...f, name: u.name, email: u.email, phone: u.phone }));
        setStep(1);
      }
    } catch (err) {
      setAuthError(err.response?.data?.detail || 'Erreur de connexion');
    }
  };

  const deliveryFee = delivery === 'delivery' && selectedZone ? selectedZone.fee : 0;
  const walletCredit = useWallet ? Math.min(walletBalance, total + deliveryFee) : 0;
  const grandTotal = Math.max(0, total + deliveryFee - walletCredit);

  const handleConfirm = async () => {
    try {
      const { data } = await axios.post(`${API}/api/orders`, {
        items, total: grandTotal, subtotal: total, delivery, form, method, customer_id: user?.id,
        delivery_zone: selectedZone || null, delivery_fee: deliveryFee,
        wallet_credit: walletCredit,
      });
      if (walletCredit > 0 && user?.id) {
        await axios.post(`${API}/api/customers/${user.id}/wallet/deduct`, { amount: walletCredit }).catch(() => {});
      }
      setOrderId(data.order_id);
    } catch {}
    setConfirmed(true);
  };

  const skipLogin = () => { setStep(1); };

  const stepLabels = ['Connexion', 'Méthodes', 'Détails', 'Confirmer'];

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 z-[95] flex items-center justify-center p-4" onClick={onClose}>
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
          className="w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl flex flex-col"
          style={{ backgroundColor: '#181D17' }} onClick={e => e.stopPropagation()} data-testid="checkout-modal">

          <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: '#2C3529' }}>
            <h2 className="text-xl font-semibold text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Finaliser la commande</h2>
            <button onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
          </div>

          <div className="flex items-center gap-2 px-5 py-3 border-b" style={{ borderColor: '#2C3529' }}>
            {stepLabels.map((s, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step > i ? 'bg-[#6DAE65] text-white' : step === i ? 'bg-[#F78A7D] text-white' : 'bg-white/10 text-white/40'}`}>
                  {step > i ? <Check size={14} /> : i + 1}
                </div>
                <span className={`text-xs font-medium hidden sm:inline ${step === i ? 'text-white' : 'text-white/40'}`}>{s}</span>
                {i < 3 && <ChevronRight size={14} className="text-white/20 mx-1" />}
              </div>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto">
            {confirmed ? (
              <div className="flex flex-col items-center justify-center py-10 px-6">
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="w-16 h-16 rounded-full bg-[#6DAE65] flex items-center justify-center mb-4">
                  <Check size={32} className="text-white" />
                </motion.div>
                <h3 className="text-2xl font-semibold text-white mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Commande confirmee !</h3>
                <p className="text-white/60 text-center text-sm mb-6">Merci ! Vous recevrez un courriel de confirmation.</p>

                {/* Mini Invoice */}
                <div className="w-full max-w-md rounded-xl p-5 space-y-3 text-sm" style={{ backgroundColor: '#0E120D', border: '1px solid #2C3529' }}>
                  <div className="flex justify-between items-center border-b pb-2" style={{ borderColor: '#2C3529' }}>
                    <span className="text-xs font-bold uppercase tracking-wider text-white/40">Facture</span>
                    <span className="text-xs text-white/30">#{orderId?.slice(0, 8)}</span>
                  </div>
                  <div className="text-xs text-white/40">
                    <p className="font-semibold text-white/70">Concept Gourmet Traiteur</p>
                    <p>1335, boul. Bona-Dussault, St-Marc-des-Carrieres, QC G0A 4B0</p>
                    <p>418-285-7251 | info@conceptgourmet.ca</p>
                  </div>
                  <div className="border-t pt-2 space-y-1" style={{ borderColor: '#2C3529' }}>
                    <p className="text-white/70"><span className="text-white/40">Client:</span> {form.name}</p>
                    <p className="text-white/70"><span className="text-white/40">Courriel:</span> {form.email}</p>
                    {form.phone && <p className="text-white/70"><span className="text-white/40">Tel:</span> {form.phone}</p>}
                    <p className="text-white/70"><span className="text-white/40">Date:</span> {form.date ? new Date(form.date + 'T12:00:00').toLocaleDateString('fr-CA', { weekday: 'long', day: 'numeric', month: 'long' }) : 'N/A'}</p>
                    <p className="text-white/70"><span className="text-white/40">Mode:</span> {delivery === 'delivery' ? 'Livraison' : 'Cueillette'}</p>
                  </div>
                  <div className="border-t pt-2 space-y-1" style={{ borderColor: '#2C3529' }}>
                    {items.map((item, i) => (
                      <div key={i} className="flex justify-between">
                        <span className="text-white/60">{item.name} ({item.format}) x{item.qty}</span>
                        <span className="text-white/80">{(item.price * item.qty).toFixed(2)} $</span>
                      </div>
                    ))}
                  </div>
                  <div className="border-t pt-2 space-y-1" style={{ borderColor: '#2C3529' }}>
                    <div className="flex justify-between text-white/50">
                      <span>Sous-total</span><span>{total.toFixed(2)} $</span>
                    </div>
                    {deliveryFee > 0 && (
                      <div className="flex justify-between text-white/50">
                        <span>Livraison</span><span>{deliveryFee.toFixed(2)} $</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-white pt-1">
                      <span>Total</span><span className="text-[#F78A7D]">{grandTotal.toFixed(2)} $</span>
                    </div>
                  </div>
                </div>

                <button onClick={() => { window.print(); }} className="mt-4 px-6 py-2 rounded-xl text-xs font-medium text-white/60 hover:text-white transition-colors" style={{ border: '1px solid #2C3529' }}>
                  Imprimer la facture
                </button>
                <button onClick={() => { clearCart(); onClose(); setStep(user ? 1 : 0); setMethod(null); setConfirmed(false); setOrderId(null); setSelectedZone(null);
                  setForm({ name: user?.name || '', email: user?.email || '', phone: user?.phone || '', address: '', date: '', notes: '' }); }}
                  className="mt-2 text-xs text-white/30 hover:text-white/50 transition-colors">Fermer</button>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2">
                {/* Left - Order summary */}
                <div className="p-5 border-b lg:border-b-0 lg:border-r" style={{ borderColor: '#2C3529' }}>
                  <h3 className="text-sm font-semibold text-white/70 mb-4">Récapitulatif ({items.length} articles)</h3>
                  <div className="space-y-3 max-h-[40vh] overflow-y-auto pr-2">
                    {items.map(item => (
                      <div key={item.key} className="flex gap-3 p-3 rounded-xl" style={{ backgroundColor: '#121611', border: '1px solid #2C3529' }}>
                        {item.image && <img src={item.image} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white truncate">{item.name}</p>
                          <p className="text-xs text-white/40">{item.format} × {item.qty}</p>
                        </div>
                        <p className="text-sm font-bold text-[#F78A7D]">{(item.price * item.qty).toFixed(2)} $</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-4 border-t flex flex-col gap-1 text-sm" style={{ borderColor: '#2C3529' }}>
                    <div className="flex justify-between text-white/60">
                      <span>Sous-total</span>
                      <span>{total.toFixed(2)} $</span>
                    </div>
                    {delivery === 'delivery' && selectedZone && (
                      <div className="flex justify-between text-white/60">
                        <span>Livraison ({selectedZone.name})</span>
                        <span>{selectedZone.fee.toFixed(2)} $</span>
                      </div>
                    )}
                    {walletBalance > 0 && user && (
                      <div className="flex items-center justify-between py-2 mt-1 rounded-lg px-2" style={{ backgroundColor: 'rgba(234,179,8,0.08)' }}>
                        <label className="flex items-center gap-2 cursor-pointer text-sm">
                          <input type="checkbox" checked={useWallet} onChange={e => setUseWallet(e.target.checked)}
                            className="w-4 h-4 rounded accent-[#EAB308]" />
                          <span style={{ color: '#EAB308' }}>Utiliser mon solde ({walletBalance.toFixed(2)} $)</span>
                        </label>
                        {useWallet && <span className="font-medium" style={{ color: '#6DAE65' }}>-{walletCredit.toFixed(2)} $</span>}
                      </div>
                    )}
                    <div className="flex justify-between text-lg font-bold text-white pt-1">
                      <span>Total</span>
                      <span className="text-[#F78A7D]">{grandTotal.toFixed(2)} $</span>
                    </div>
                  </div>
                </div>

                {/* Right - Steps */}
                <div className="p-5">
                  {/* Step 0: Login */}
                  {step === 0 && (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 mb-2">
                        <User size={18} className="text-[#F78A7D]" />
                        <h3 className="text-sm font-semibold text-white/70">
                          {authMode === 'login' ? 'Connexion' : 'Créer un compte'}
                        </h3>
                      </div>

                      <div className="flex gap-2 mb-4">
                        <button onClick={() => setAuthMode('login')}
                          className={`flex-1 py-2 rounded-xl text-xs font-semibold transition-all ${authMode === 'login' ? 'text-white ring-1 ring-[#F78A7D]' : 'text-white/50'}`}
                          style={{ backgroundColor: authMode === 'login' ? 'rgba(247,138,125,0.15)' : '#121611', border: '1px solid #2C3529' }}>
                          Se connecter
                        </button>
                        <button onClick={() => setAuthMode('register')}
                          className={`flex-1 py-2 rounded-xl text-xs font-semibold transition-all ${authMode === 'register' ? 'text-white ring-1 ring-[#6DAE65]' : 'text-white/50'}`}
                          style={{ backgroundColor: authMode === 'register' ? 'rgba(109,174,101,0.15)' : '#121611', border: '1px solid #2C3529' }}>
                          Créer un compte
                        </button>
                      </div>

                      {authMode === 'register' && (
                        <>
                          <div className="flex gap-2 mb-3">
                            <button onClick={() => setAuthForm(f => ({ ...f, type: 'individual' }))}
                              className={`flex-1 py-2.5 rounded-xl text-xs font-medium transition-all ${authForm.type === 'individual' ? 'text-white bg-[#F78A7D]/15 ring-1 ring-[#F78A7D]' : 'text-white/50'}`}
                              style={{ border: '1px solid #2C3529' }}>
                              Individuel
                            </button>
                            <button onClick={() => setAuthForm(f => ({ ...f, type: 'corporate' }))}
                              className={`flex-1 py-2.5 rounded-xl text-xs font-medium transition-all ${authForm.type === 'corporate' ? 'text-white bg-[#6DAE65]/15 ring-1 ring-[#6DAE65]' : 'text-white/50'}`}
                              style={{ border: '1px solid #2C3529' }}>
                              Corporatif
                            </button>
                          </div>
                          <input type="text" placeholder="Nom complet *" value={authForm.name} onChange={e => setAuthForm(f => ({ ...f, name: e.target.value }))}
                            className="w-full px-4 py-3 rounded-xl text-sm text-white outline-none" style={{ backgroundColor: '#121611', border: '1px solid #2C3529' }} />
                          <input type="tel" placeholder="Téléphone" value={authForm.phone} onChange={e => setAuthForm(f => ({ ...f, phone: e.target.value }))}
                            className="w-full px-4 py-3 rounded-xl text-sm text-white outline-none" style={{ backgroundColor: '#121611', border: '1px solid #2C3529' }} />
                          {authForm.type === 'corporate' && (
                            <input type="text" placeholder="Nom de l'entreprise *" value={authForm.company} onChange={e => setAuthForm(f => ({ ...f, company: e.target.value }))}
                              className="w-full px-4 py-3 rounded-xl text-sm text-white outline-none" style={{ backgroundColor: '#121611', border: '1px solid #2C3529' }} />
                          )}
                        </>
                      )}

                      <input type="email" placeholder="Courriel *" value={authForm.email} onChange={e => setAuthForm(f => ({ ...f, email: e.target.value }))}
                        className="w-full px-4 py-3 rounded-xl text-sm text-white outline-none" style={{ backgroundColor: '#121611', border: '1px solid #2C3529' }} data-testid="checkout-email" />
                      <input type="password" placeholder="Mot de passe *" value={authForm.password} onChange={e => setAuthForm(f => ({ ...f, password: e.target.value }))}
                        onKeyDown={e => e.key === 'Enter' && handleAuth()}
                        className="w-full px-4 py-3 rounded-xl text-sm text-white outline-none" style={{ backgroundColor: '#121611', border: '1px solid #2C3529' }} data-testid="checkout-password" />

                      {authError && <p className="text-red-400 text-xs">{authError}</p>}

                      <button onClick={handleAuth}
                        className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-all hover:scale-[1.02] flex items-center justify-center gap-2"
                        style={{ background: 'linear-gradient(135deg, #F78A7D, #E8665A)' }} data-testid="checkout-auth-btn">
                        <LogIn size={16} /> {authMode === 'login' ? 'Se connecter' : 'Créer mon compte'}
                      </button>

                      <button onClick={skipLogin} className="w-full text-center text-xs text-white/40 hover:text-white/60 transition-colors py-2">
                        Continuer sans compte →
                      </button>
                    </div>
                  )}

                  {/* Step 1: Payment methods */}
                  {step === 1 && (
                    <div>
                      {user && (
                        <div className="flex items-center gap-2 mb-4 p-3 rounded-xl" style={{ backgroundColor: '#121611', border: '1px solid #2C3529' }}>
                          <div className="w-8 h-8 rounded-full bg-[#6DAE65] flex items-center justify-center text-white text-xs font-bold">
                            {user.name?.charAt(0)?.toUpperCase() || 'U'}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-white">{user.name}</p>
                            <p className="text-xs text-white/40">{user.email} {user.type === 'corporate' && `• ${user.company}`}</p>
                          </div>
                        </div>
                      )}
                      <h3 className="text-sm font-semibold text-white/70 mb-2">Mode de paiement</h3>
                      <div className="space-y-2">
                        {PAYMENT_METHODS.map(pm => (
                          <button key={pm.id} onClick={() => setMethod(pm.id)}
                            className={`w-full flex items-center gap-3 p-3.5 rounded-xl text-left transition-all ${method === pm.id ? 'ring-1 ring-[#F78A7D]' : ''}`}
                            style={{ backgroundColor: method === pm.id ? 'rgba(247,138,125,0.1)' : '#121611', border: '1px solid #2C3529' }}>
                            <div className="flex-shrink-0">
                              {pm.logo ? <pm.logo /> : <pm.icon size={20} className={method === pm.id ? 'text-[#F78A7D]' : 'text-white/40'} />}
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-white">{pm.label}</p>
                              <p className="text-xs text-white/40">{pm.desc}</p>
                            </div>
                            <ChevronRight size={16} className="text-white/20" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Step 2: Details */}
                  {step === 2 && (
                    <div className="space-y-4">
                      <h3 className="text-sm font-semibold text-white/70 mb-2">Détails de la commande</h3>
                      <div className="flex gap-2">
                        <button onClick={() => setDelivery('pickup')}
                          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium transition-all ${delivery === 'pickup' ? 'text-white ring-1 ring-[#F78A7D]' : 'text-white/50'}`}
                          style={{ backgroundColor: delivery === 'pickup' ? 'rgba(247,138,125,0.1)' : '#121611', border: '1px solid #2C3529' }}>
                          <Store size={16} /> Cueillette
                        </button>
                        <button onClick={() => setDelivery('delivery')}
                          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium transition-all ${delivery === 'delivery' ? 'text-white ring-1 ring-[#6DAE65]' : 'text-white/50'}`}
                          style={{ backgroundColor: delivery === 'delivery' ? 'rgba(109,174,101,0.1)' : '#121611', border: '1px solid #2C3529' }}>
                          <Truck size={16} /> Livraison
                        </button>
                      </div>
                      <input type="text" placeholder="Nom complet *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                        className="w-full px-4 py-3 rounded-xl text-sm text-white outline-none" style={{ backgroundColor: '#121611', border: '1px solid #2C3529' }} />
                      <div className="grid grid-cols-2 gap-3">
                        <input type="email" placeholder="Courriel *" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                          className="w-full px-4 py-3 rounded-xl text-sm text-white outline-none" style={{ backgroundColor: '#121611', border: '1px solid #2C3529' }} />
                        <input type="tel" placeholder="514 555-1234" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: formatPhone(e.target.value) }))}
                          className="w-full px-4 py-3 rounded-xl text-sm text-white outline-none" style={{ backgroundColor: '#121611', border: '1px solid #2C3529' }} />
                      </div>
                      {delivery === 'delivery' && (
                        <>
                          <input type="text" placeholder="Adresse de livraison *" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                            className="w-full px-4 py-3 rounded-xl text-sm text-white outline-none" style={{ backgroundColor: '#121611', border: '1px solid #2C3529' }} />
                          {/* Zone de livraison */}
                          <div>
                            <p className="text-xs font-semibold text-white/50 mb-2 flex items-center gap-1.5">
                              <Truck size={14} className="text-[#6DAE65]" /> Zone de livraison
                            </p>
                            <div className="space-y-1.5">
                              {zones.map(zone => (
                                <button key={zone.id} onClick={() => setSelectedZone(zone)} type="button"
                                  className={`w-full flex items-center justify-between p-3 rounded-xl text-left text-sm transition-all ${selectedZone?.id === zone.id ? 'ring-1 ring-[#6DAE65]' : ''}`}
                                  style={{ backgroundColor: selectedZone?.id === zone.id ? 'rgba(109,174,101,0.1)' : '#121611', border: '1px solid #2C3529' }}>
                                  <div>
                                    <p className="text-white font-medium">{zone.name}</p>
                                    <p className="text-[10px] text-white/30">Min. commande: {zone.min_order?.toFixed(2)} $</p>
                                  </div>
                                  <span className="font-bold" style={{ color: '#EAB308' }}>{zone.fee?.toFixed(2)} $</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        </>
                      )}

                      {/* Calendar */}
                      <div>
                        <p className="text-xs font-semibold text-white/50 mb-2 flex items-center gap-1.5">
                          <CalendarDays size={14} className="text-[#F78A7D]" />
                          {delivery === 'pickup' ? 'Date de cueillette' : 'Date de livraison'}
                        </p>
                        <MiniCalendar selected={form.date} onSelect={(d) => setForm(f => ({ ...f, date: d }))} />
                        {form.date && (
                          <p className="text-xs text-[#6DAE65] mt-2 text-center font-medium">
                            {new Date(form.date + 'T12:00:00').toLocaleDateString('fr-CA', { weekday: 'long', day: 'numeric', month: 'long' })}
                          </p>
                        )}
                      </div>

                      <textarea rows={2} placeholder="Notes spéciales (optionnel)" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                        className="w-full px-4 py-3 rounded-xl text-sm text-white outline-none resize-none" style={{ backgroundColor: '#121611', border: '1px solid #2C3529' }} />
                    </div>
                  )}

                  {/* Step 3: Confirm */}
                  {step === 3 && (
                    <div className="space-y-4">
                      <h3 className="text-sm font-semibold text-white/70 mb-2">Confirmer votre commande</h3>
                      <div className="p-4 rounded-xl space-y-2 text-sm" style={{ backgroundColor: '#121611', border: '1px solid #2C3529' }}>
                        <div className="flex justify-between"><span className="text-white/50">Nom</span><span className="text-white">{form.name}</span></div>
                        <div className="flex justify-between"><span className="text-white/50">Courriel</span><span className="text-white">{form.email}</span></div>
                        <div className="flex justify-between"><span className="text-white/50">Téléphone</span><span className="text-white">{form.phone}</span></div>
                        <div className="flex justify-between"><span className="text-white/50">Mode</span><span className="text-white">{delivery === 'pickup' ? 'Cueillette' : 'Livraison'}</span></div>
                        {delivery === 'delivery' && <div className="flex justify-between"><span className="text-white/50">Adresse</span><span className="text-white text-right">{form.address}</span></div>}
                        {form.date && <div className="flex justify-between"><span className="text-white/50">Date</span><span className="text-white">{form.date}</span></div>}
                        <div className="flex justify-between"><span className="text-white/50">Paiement</span>
                          <span className="text-white">{PAYMENT_METHODS.find(p => p.id === method)?.label || 'Non sélectionné'}</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t font-bold text-base" style={{ borderColor: '#2C3529' }}>
                          <span className="text-white">Total</span><span className="text-[#F78A7D]">{total.toFixed(2)} $</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {!confirmed && (
            <div className="flex items-center justify-between p-5 border-t" style={{ borderColor: '#2C3529' }}>
              <button onClick={() => step === 0 ? onClose() : setStep(step - 1)}
                className="text-sm text-white/60 hover:text-white transition-colors">
                {step === 0 ? 'Annuler' : 'Retour'}
              </button>
              {step < 3 ? (
                <button onClick={() => setStep(step + 1)}
                  disabled={step === 1 && !method}
                  className="px-6 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:scale-105 disabled:opacity-40"
                  style={{ background: 'linear-gradient(135deg, #F78A7D, #E8665A)' }}>
                  Suivant
                </button>
              ) : (
                <button onClick={handleConfirm}
                  className="px-6 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:scale-105"
                  style={{ background: 'linear-gradient(135deg, #6DAE65, #5A9A55)' }}>
                  Confirmer la commande
                </button>
              )}
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
